import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import DOMPurify from 'dompurify';
import { 
  Mail, 
  Search, 
  RefreshCw, 
  Star, 
  Archive, 
  Trash2, 
  Filter,
  Reply,
  ReplyAll,
  Forward,
  ChevronRight,
  ChevronDown,
  Clock,
  User,
  Paperclip,
  CheckCircle,
  Circle,
  Inbox,
  Folder,
  Settings,
  Send,
  FileText,
  Tag,
  Plus,
  X,
  FolderOpen,
  Check,
  Users,
  Pencil
} from 'lucide-react';
import EmailModal from '../components/EmailModal';
import EmailAttachmentPreviewModal from '../components/EmailAttachmentPreviewModal';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';
import Massenmail from './Massenmail';

interface EmailLabel {
  id: number;
  name: string;
  color: string;
  icon: string;
  is_system: boolean;
}

interface Email {
  seqno: number;
  uid: number;
  id?: number; // Database ID for label assignments
  headers: {
    from: string;
    to: string;
    cc?: string;
    subject: string;
    date: Date;
    messageId: string;
    inReplyTo?: string;
    references?: string[];
  };
  body: string;
  html?: string;
  text?: string;
  attachments: any[];
  flags: string[];
  date: Date;
  labels?: EmailLabel[]; // Labels assigned to this email
  llm_summary?: string; // LLM-generated summary (max 10 words)
}

interface EmailAttachment {
  index: number;
  filename: string;
  contentType: string;
  size: number;
}

interface EmailThread {
  threadId: string;
  subject: string;
  emails: Email[];
  lastActivity: Date;
}

const EMAIL_CLIENT_STATE_KEY = 'lernrakete-email-client-state';
const EMAIL_CLIENT_HAD_CREDENTIALS_KEY = 'lernrakete-email-client-had-credentials';

function loadEmailClientState(): { viewMode?: string; mailbox?: string; showScheduledView?: boolean; showElternbriefeView?: boolean; selectedEmailId?: number } {
  try {
    const raw = localStorage.getItem(EMAIL_CLIENT_STATE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveEmailClientState(state: { viewMode?: string; mailbox?: string; showScheduledView?: boolean; showElternbriefeView?: boolean; selectedEmailId?: number | null }) {
  try {
    localStorage.setItem(EMAIL_CLIENT_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function loadHadCredentials(): boolean {
  try {
    return localStorage.getItem(EMAIL_CLIENT_HAD_CREDENTIALS_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveHadCredentials(had: boolean) {
  try {
    localStorage.setItem(EMAIL_CLIENT_HAD_CREDENTIALS_KEY, had ? 'true' : 'false');
  } catch {
    // ignore
  }
}

/** Kategorie-Label: getönter Hintergrund + Label-Farbe (Showcase/Detail-Ansicht), nicht weiße Vollfläche */
function emailLabelChipStyle(color?: string): { backgroundColor: string; color: string; borderColor: string } {
  const c = color?.trim() || '#6B7280';
  return {
    backgroundColor: `${c}22`,
    color: c,
    borderColor: `${c}66`
  };
}

export default function EmailClient() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [emails, setEmails] = useState<Email[]>([]);
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [viewMode, setViewMode] = useState<'inbox' | 'threads'>(() => {
    const s = loadEmailClientState();
    return s.viewMode === 'threads' ? 'threads' : 'inbox';
  });
  const [mailbox, setMailbox] = useState<string>(() => {
    const s = loadEmailClientState();
    return typeof s.mailbox === 'string' && s.mailbox ? s.mailbox : 'INBOX';
  });
  const [mailboxConfig, setMailboxConfig] = useState<{sent: string | null, drafts: string | null, inbox: string}>({
    sent: null,
    drafts: null,
    inbox: 'INBOX'
  });
  const [detectingMailboxes, setDetectingMailboxes] = useState(false);
  const [hasTriedAutoDetect, setHasTriedAutoDetect] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const sanitizedSelectedEmailHtml = useMemo(
    () => (selectedEmail?.html ? DOMPurify.sanitize(selectedEmail.html) : ''),
    [selectedEmail?.html]
  );

  const restoreSelectedEmailIdRef = useRef<number | null>(null);
  const [emailContacts, setEmailContacts] = useState<Record<string, {name: string, photo_url?: string, contact_type?: string, reference_id?: string}>>({});
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string>>({});
  const [studentClasses, setStudentClasses] = useState<Record<string, string>>({}); // UNID -> Klasse
  const [emailBodyLoading, setEmailBodyLoading] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState<EmailAttachment[]>([]);
  const [emailAttachmentsLoading, setEmailAttachmentsLoading] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<{
    emailId: number;
    index: number;
    filename: string;
    contentType: string;
  } | null>(null);
  const [linkedStudents, setLinkedStudents] = useState<Record<number, Array<{sophomorix_unid: string, student_name: string, klasse: string, file_id?: string}>>>({}); // email_id -> linked students
  const [showStudentSelectModal, setShowStudentSelectModal] = useState(false);
  const [emailForStudentSelect, setEmailForStudentSelect] = useState<Email | null>(null);
  const [selectedStudentsForFile, setSelectedStudentsForFile] = useState<string[]>([]);
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [editingDraft, setEditingDraft] = useState<{
    uid: number;
    folder: string;
    subject: string;
    to?: string;
    cc?: string;
  } | null>(null);
  const [replyMode, setReplyMode] = useState<'reply' | 'replyAll' | 'forward' | undefined>(undefined);
  const [originalEmail, setOriginalEmail] = useState<{
    from: string;
    to: string;
    cc?: string;
    subject: string;
    body: string;
    html?: string;
    date: Date | string;
    messageId?: string;
    inReplyTo?: string;
  } | undefined>(undefined);
  const [filter, setFilter] = useState<'all' | 'unread' | 'starred'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'from' | 'subject'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [emailLabels, setEmailLabels] = useState<Record<number, EmailLabel[]>>({}); // email_id -> labels
  const [showLabelMenuInList, setShowLabelMenuInList] = useState<number | null>(null); // email_id for which label menu is open in list
  const [showLabelMenuInDetail, setShowLabelMenuInDetail] = useState<boolean>(false); // whether label menu is open in detail view
  const labelMenuRef = useRef<HTMLDivElement>(null);
  const [showScheduledView, setShowScheduledView] = useState(() => {
    const s = loadEmailClientState();
    return !!s.showScheduledView;
  });
  const [showElternbriefeView, setShowElternbriefeView] = useState(() => {
    const s = loadEmailClientState();
    return !!s.showElternbriefeView;
  });
  // Beim Wiedereintritt sofort „hatte Credentials“ aus localStorage nutzen, damit die Inbox-Query nicht auf die Credentials-API warten muss.
  const hadCredentialsLastTime = loadHadCredentials();
  const [hasNoEmailCredentials, setHasNoEmailCredentials] = useState(() => !hadCredentialsLastTime);
  const [credentialsChecked, setCredentialsChecked] = useState(() => hadCredentialsLastTime);
  const [threadsLoading, setThreadsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // When redirected from /elternbriefe, open the Elternbriefe tab
  useEffect(() => {
    if ((location.state as { tab?: string })?.tab === 'elternbriefe') {
      setShowElternbriefeView(true);
      setShowScheduledView(false);
    }
  }, [location.state]);

  // Check if user has any email account (on enter and when errors indicate no credentials)
  const { data: credentialsData, isLoading: credentialsLoading } = useQuery(
    ['email-client', 'credentials'],
    async () => {
      const response = await api.get('/email/credentials');
      return response.data?.data ?? [];
    },
    {
      staleTime: 2 * 60 * 1000,
      onSuccess: (list: any[]) => {
        setCredentialsChecked(true);
        const hasAnyEnabled = Array.isArray(list) && list.some((c: any) => c.enabled !== false && c.enabled !== 0);
        setHasNoEmailCredentials(!list?.length || !hasAnyEnabled);
      }
    }
  );

  // Bei Wiedereintritt liefert die Query oft nur Cache – onSuccess läuft dann nicht. State aus Daten ableiten und für nächsten Besuch merken.
  useEffect(() => {
    if (credentialsData === undefined) return;
    setCredentialsChecked(true);
    const list = Array.isArray(credentialsData) ? credentialsData : [];
    const hasAnyEnabled = list.some((c: any) => c.enabled !== false && c.enabled !== 0);
    const noCreds = !list.length || !hasAnyEnabled;
    setHasNoEmailCredentials(noCreds);
    saveHadCredentials(!noCreds);
  }, [credentialsData]);

  // Transform email data helper
  const transformEmail = (email: any) => {
    const headers = email.headers || {};
    const transformed = {
      ...email,
      // Explicitly preserve id for label assignments
      id: email.id,
      uid: email.uid,
      headers: {
        from: headers.from || email.from || 'Unbekannter Absender',
        to: headers.to || email.to || '',
        cc: headers.cc || email.cc || '',
        subject: headers.subject || email.subject || '(Kein Betreff)',
        date: headers.date ? new Date(headers.date) : (email.date ? new Date(email.date) : new Date()),
        messageId: headers.messageId || email.messageId || '',
        inReplyTo: headers.inReplyTo || email.inReplyTo || '',
        references: headers.references || email.references || []
      },
      flags: email.flags || [],
      attachments: email.attachments || [],
      body: email.body || email.text || '',
      html: email.html || '',
      text: email.text || email.body || '',
      date: email.date ? new Date(email.date) : (headers.date ? new Date(headers.date) : new Date())
    };
    
    // Debug: Log if email is missing id
    if (!transformed.id) {
      console.warn('[EmailClient] Email missing id after transform:', {
        uid: transformed.uid,
        subject: transformed.headers?.subject,
        originalEmail: email
      });
    }
    
    return transformed;
  };

  // Use actual mailbox name from config
  const actualMailbox = useMemo(() => {
    return mailbox === mailboxConfig.inbox ? mailboxConfig.inbox : 
           mailbox === mailboxConfig.sent ? (mailboxConfig.sent || mailbox) :
           mailbox === mailboxConfig.drafts ? (mailboxConfig.drafts || mailbox) :
           mailbox;
  }, [mailbox, mailboxConfig]);

  const isDraftsFolder = !!(mailboxConfig.drafts && mailbox === mailboxConfig.drafts);

  const openDraftForEditing = useCallback((email: Email) => {
    setReplyMode(undefined);
    setOriginalEmail(undefined);
    setEditingDraft({
      uid: email.uid,
      folder: actualMailbox,
      subject: getEmailSubject(email),
      to: getEmailTo(email) || undefined,
      cc: getEmailCc(email) || undefined,
    });
    setShowComposeModal(true);
  }, [actualMailbox]);

  // Determine search query
  const searchQuery = useMemo(() => {
    if (mailbox === mailboxConfig.inbox) {
      return filter === 'unread' ? 'UNSEEN' : 'ALL';
    }
    return 'ALL';
  }, [mailbox, mailboxConfig.inbox, filter]);

  // Fetch emails with React Query (cached for 2 minutes) – only when user has email credentials
  const { data: emailsData, isLoading: loading, refetch: refetchEmails } = useQuery(
    ['email-client', 'emails', actualMailbox, searchQuery, filter, currentPage],
    async () => {
      const offset = (currentPage - 1) * limit;
      const response = await api.get('/email-client/inbox', {
        params: {
          limit,
          offset,
          searchQuery,
          box: actualMailbox,
          forceSync: viewMode === 'inbox'
        }
      });
      return response.data;
    },
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      enabled: credentialsChecked && !hasNoEmailCredentials && !!actualMailbox,
      keepPreviousData: true, // Keep previous data while loading new page
      refetchOnMount: true, // Always refetch on mount so re-entering the page shows fresh data
      refetchInterval: viewMode === 'inbox' ? 60 * 1000 : false, // auto-refresh inbox
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        if (data?.success) {
          const transformedEmails = (data.data || []).map(transformEmail);
          if (currentPage === 1) {
            setEmails(transformedEmails);
          } else {
            // Append new emails, avoiding duplicates
            setEmails(prev => {
              const existingUids = new Set(prev.map((e: Email) => e.uid));
              const newEmails = transformedEmails.filter((e: Email) => !existingUids.has(e.uid));
              return [...prev, ...newEmails];
            });
          }
          const fetchedCount = transformedEmails.length;
          setHasMore(fetchedCount === limit);
          setTotalCount(data.pagination?.count || transformedEmails.length);
        }
      },
      onError: (error: any) => {
        console.error('Error fetching emails:', error);
        if (error.response?.data?.code === 'NO_EMAIL_CREDENTIALS') {
          setHasNoEmailCredentials(true);
          return;
        }
        toast.error('Fehler beim Laden der E-Mails: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  // Bei Wiedereintritt liefert die Inbox-Query oft nur Cache – onSuccess läuft dann nicht.
  // Liste aus emailsData übernehmen (Seite 1), auch wenn sich die Referenz nicht geändert hat.
  useEffect(() => {
    if (!emailsData?.success || currentPage !== 1) return;
    const raw = emailsData.data || [];
    if (raw.length === 0 && emails.length > 0) return; // Avoid clearing a populated list with an empty transitional value
    const transformedEmails = raw.map(transformEmail);
    setEmails(transformedEmails);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailsData]);

  // Fetch scheduled emails (for "Geplant" view)
  const { data: scheduledEmailsData, isLoading: scheduledLoading, refetch: refetchScheduled } = useQuery(
    ['email-client', 'scheduled'],
    async () => {
      const response = await api.get('/email/scheduled');
      return response.data?.data || [];
    },
    { enabled: showScheduledView, staleTime: 30 * 1000 }
  );
  const scheduledEmails = Array.isArray(scheduledEmailsData) ? scheduledEmailsData : [];

  // Fetch new Elternbrief responses count for badge (last 7 days)
  const { data: newResponsesData, refetch: refetchNewResponses } = useQuery(
    ['massenmail', 'new-responses-count'],
    async () => {
      const response = await api.get('/massenmail/new-responses-count');
      return response.data?.data?.count ?? 0;
    },
    {
      staleTime: 30 * 1000,
      refetchInterval: showElternbriefeView ? 60 * 1000 : false,
      refetchOnWindowFocus: true,
      enabled: credentialsChecked
    }
  );
  const newResponsesCount = typeof newResponsesData === 'number' ? newResponsesData : 0;

  // Refresh badge when switching to Elternbriefe tab
  useEffect(() => {
    if (showElternbriefeView) {
      refetchNewResponses();
    }
  }, [showElternbriefeView, refetchNewResponses]);

  const cancelScheduledEmail = async (id: number) => {
    try {
      await api.delete(`/email/scheduled/${id}`);
      toast.success('Geplante E-Mail wurde abgebrochen');
      queryClient.invalidateQueries(['email-client', 'scheduled']);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Fehler beim Abbrechen');
    }
  };

  // Load more emails function
  const loadMoreEmails = useCallback(() => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasMore, loading]);

  // Fetch email threads
  const fetchThreads = async () => {
    setThreadsLoading(true);
    try {
      const offset = (currentPage - 1) * limit;
      
      const response = await api.get('/email-client/threads', {
        params: {
          limit,
          offset
        }
      });

      if (response.data.success) {
        // Transform thread data
        const transformedThreads = (response.data.data || []).map((thread: any) => {
          const transformedEmails = (thread.emails || []).map((email: any) => {
            const headers = email.headers || {};
            return {
              ...email,
              headers: {
                from: headers.from || email.from || 'Unbekannter Absender',
                to: headers.to || email.to || '',
                cc: headers.cc || email.cc || '',
                subject: headers.subject || email.subject || '(Kein Betreff)',
                date: headers.date ? new Date(headers.date) : (email.date ? new Date(email.date) : new Date()),
                messageId: headers.messageId || email.messageId || '',
                inReplyTo: headers.inReplyTo || email.inReplyTo || '',
                references: headers.references || email.references || []
              },
              flags: email.flags || [],
              attachments: email.attachments || [],
              body: email.body || email.text || '',
              html: email.html || '',
              text: email.text || email.body || '',
              date: email.date ? new Date(email.date) : (headers.date ? new Date(headers.date) : new Date())
            };
          });
          
          return {
            ...thread,
            emails: transformedEmails,
            subject: thread.subject || '(Kein Betreff)',
            lastActivity: thread.lastActivity ? new Date(thread.lastActivity) : new Date()
          };
        });
        setThreads(transformedThreads);
        setTotalCount(response.data.pagination?.count || transformedThreads.length);
      }
    } catch (error: any) {
      console.error('Error fetching threads:', error);
      toast.error('Fehler beim Laden der Threads: ' + (error.response?.data?.message || error.message));
    } finally {
      setThreadsLoading(false);
    }
  };

  // Mark email as read/unread
  const toggleReadStatus = async (email: Email, isRead: boolean) => {
    try {
      if (isRead) {
        await api.post('/email-client/mark-read', {
          uid: email.uid,
          box: 'INBOX'
        });
      } else {
        await api.post('/email-client/mark-unread', {
          uid: email.uid,
          box: 'INBOX'
        });
      }

      // Update local state
      setEmails(prev => prev.map(e => 
        e.uid === email.uid 
          ? { ...e, flags: isRead ? [...e.flags, '\\Seen'] : e.flags.filter(f => f !== '\\Seen') }
          : e
      ));

      toast.success(isRead ? 'E-Mail als gelesen markiert' : 'E-Mail als ungelesen markiert');
    } catch (error: any) {
      console.error('Error toggling read status:', error);
      toast.error('Fehler beim Aktualisieren: ' + (error.response?.data?.message || error.message));
    }
  };

  // Format relative time (e.g., "vor 25 Min")
  const formatRelativeTime = (date: Date | string) => {
    let d: Date;
    if (typeof date === 'string') {
      d = new Date(date);
    } else {
      d = date;
    }
    
    // Check if date is valid
    if (isNaN(d.getTime())) {
      return 'Ungültiges Datum';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // If date is in the future
    if (diffMs < 0) {
      return 'in Zukunft';
    }
    
    if (diffMins < 1) {
      return 'gerade eben';
    } else if (diffMins < 60) {
      return `vor ${diffMins} Min`;
    } else if (diffHours < 24) {
      return `vor ${diffHours} Std`;
    } else if (diffDays === 1) {
      return 'gestern';
    } else if (diffDays < 7) {
      return `vor ${diffDays} Tagen`;
    } else {
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  // Extract student name from email (subject, body, or contact)
  const extractStudentName = (email: Email, contact: any): string | null => {
    // First, check if contact is a student
    if (contact?.contact_type === 'student' && contact.name) {
      return contact.name;
    }
    
    // Try to extract from subject
    const subject = getEmailSubject(email);
    if (subject && subject !== '(Kein Betreff)') {
      // Common patterns: "für [Name]", "von [Name]", "[Name] -", etc.
      const patterns = [
        /für\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/i,
        /von\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/i,
        /^([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)\s*[-–—]/,
        /^([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)\s*:/,
      ];
      
      for (const pattern of patterns) {
        const match = subject.match(pattern);
        if (match && match[1]) {
          return match[1].trim();
        }
      }
    }
    
    // Try to extract from LLM summary
    if (email.llm_summary) {
      const summaryPatterns = [
        /für\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/i,
        /von\s+([A-ZÄÖÜ][a-zäöüß]+(?:\s+[A-ZÄÖÜ][a-zäöüß]+)?)/i,
      ];
      
      for (const pattern of summaryPatterns) {
        const match = email.llm_summary.match(pattern);
        if (match && match[1]) {
          return match[1].trim();
        }
      }
    }
    
    return null;
  };

  // Plain-text snippet from body for list preview (strip HTML, truncate)
  const getBodySnippet = (email: Email, maxLen = 120): string => {
    const raw = (email.text || email.body || '').trim();
    if (!raw) return '';
    const stripped = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (stripped.length <= maxLen) return stripped;
    return stripped.slice(0, maxLen).trim() + '…';
  };

  // Get meaningful subject (replace "(Kein Betreff)" with LLM summary or first words)
  const getMeaningfulSubject = (email: Email): string => {
    const subject = getEmailSubject(email);
    if (subject && subject !== '(Kein Betreff)') {
      return subject;
    }
    
    // Use LLM summary if available
    if (email.llm_summary) {
      return email.llm_summary;
    }
    
    // Use first words of email text
    if (email.text) {
      const firstWords = email.text.trim().split(/\s+/).slice(0, 5).join(' ');
      return firstWords.length > 50 ? firstWords.substring(0, 50) + '...' : firstWords;
    }
    
    return 'E-Mail ohne Betreff';
  };

  // Decode RFC 2047 encoded header values
  const decodeRFC2047 = (str: string): string => {
    if (!str) return str;
    
    // Pattern: =?charset?encoding?encoded-text?=
    // Can have multiple encoded parts in one string
    const rfc2047Pattern = /=\?([^?]+)\?([QBqb])\?([^?]+)\?=/g;
    
    return str.replace(rfc2047Pattern, (match, charset, encoding, encodedText) => {
      try {
        if (encoding.toUpperCase() === 'Q') {
          // Quoted-printable decoding
          const bytes: number[] = [];
          let i = 0;
          
          while (i < encodedText.length) {
            if (encodedText[i] === '_') {
              bytes.push(0x20); // Space
              i++;
            } else if (encodedText[i] === '=' && i + 2 < encodedText.length) {
              // Hex sequence =XX
              const hex = encodedText.substring(i + 1, i + 3);
              if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
                bytes.push(parseInt(hex, 16));
                i += 3;
              } else {
                // Invalid hex, treat as literal
                bytes.push(encodedText.charCodeAt(i));
                i++;
              }
            } else {
              bytes.push(encodedText.charCodeAt(i));
              i++;
            }
          }
          
          // Convert bytes to string using proper charset
          if (charset.toUpperCase() === 'UTF-8') {
            try {
              const decoder = new TextDecoder('utf-8');
              return decoder.decode(new Uint8Array(bytes));
            } catch (e) {
              // Fallback: try decodeURIComponent method
              try {
                const latin1String = String.fromCharCode(...bytes);
                return decodeURIComponent(escape(latin1String));
              } catch (e2) {
                // If all fails, return as latin1
                return String.fromCharCode(...bytes);
              }
            }
          } else {
            // For other charsets, just convert bytes to string
            return String.fromCharCode(...bytes);
          }
        } else if (encoding.toUpperCase() === 'B') {
          // Base64 decoding
          try {
            const base64Text = encodedText.replace(/\s/g, '');
            const binaryString = atob(base64Text);
            
            // Convert binary string to bytes
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            
            // For UTF-8, properly decode multi-byte characters
            if (charset.toUpperCase() === 'UTF-8') {
              try {
                const decoder = new TextDecoder('utf-8');
                return decoder.decode(bytes);
              } catch (e) {
                // Fallback
                return decodeURIComponent(escape(binaryString));
              }
            }
            
            return binaryString;
          } catch (e) {
            return match; // Return original if decoding fails
          }
        }
      } catch (e) {
        return match; // Return original if decoding fails
      }
      
      return match;
    });
  };

  // Safe getter for email from field
  const getEmailFrom = (email: Email) => {
    const from = email.headers?.from || 'Unbekannter Absender';
    return decodeRFC2047(from);
  };

  // Safe getter for email subject
  const getEmailSubject = (email: Email) => {
    const subject = email.headers?.subject || '(Kein Betreff)';
    return decodeRFC2047(subject);
  };

  // Safe getter for email to field
  const getEmailTo = (email: Email) => {
    const to = email.headers?.to || '';
    return decodeRFC2047(to);
  };

  // Safe getter for email cc field
  const getEmailCc = (email: Email) => {
    const cc = email.headers?.cc || '';
    return decodeRFC2047(cc);
  };

  // Safe getter for email date
  const getEmailDate = (email: Email) => {
    return email.headers?.date || email.date || new Date();
  };

  // Check if email is read
  const isRead = (email: Email) => {
    const flags = email.flags || [];
    return flags.includes('\\Seen') || flags.includes('Seen');
  };

  // Check if email is starred
  const isStarred = (email: Email) => {
    const flags = email.flags || [];
    return flags.includes('\\Flagged') || flags.includes('Flagged');
  };

  // Fetch labels with React Query (cached for 5 minutes)
  const { data: labelsData } = useQuery<EmailLabel[]>(
    ['email-labels'],
    async () => {
      const response = await api.get('/email-client-advanced/labels');
      return response.data?.data || [];
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      refetchOnWindowFocus: false,
    }
  );

  const labels = labelsData || [];

  const loadEmailLabels = useCallback(async () => {
    if (emails.length === 0) return;
    const emailIds = emails.filter(e => e.id).map(e => e.id!);
    if (emailIds.length === 0) return;

    try {
      const labelPromises = emailIds.map(async (emailId) => {
        try {
          const response = await api.get(`/email-client-advanced/emails/${emailId}/labels`);
          return { emailId, labels: response.data?.data || [] };
        } catch (error) {
          if ((error as any)?.response?.status !== 404) {
            console.error(`Error loading labels for email ${emailId}:`, error);
          }
          return { emailId, labels: [] };
        }
      });

      const results = await Promise.all(labelPromises);
      const labelsMap: Record<number, EmailLabel[]> = {};
      results.forEach(({ emailId, labels: emailLabels }) => {
        if (emailId) labelsMap[emailId] = emailLabels;
      });
      setEmailLabels(prev => ({ ...prev, ...labelsMap }));
    } catch (error) {
      console.error('Error loading email labels:', error);
    }
  }, [emails]);

  // Load labels for emails (initial + periodic refresh)
  useEffect(() => {
    loadEmailLabels();
  }, [loadEmailLabels]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadEmailLabels();
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [loadEmailLabels]);

  // Load labels for selected email
  useEffect(() => {
    if (!selectedEmail?.id) return;

    const loadSelectedEmailLabels = async () => {
      try {
        console.log('[EmailClient] Loading labels for selected email:', selectedEmail.id);
        const response = await api.get(`/email-client-advanced/emails/${selectedEmail.id}/labels`);
        if (response.data?.success) {
          console.log('[EmailClient] Labels for selected email:', response.data.data);
          setEmailLabels(prev => ({
            ...prev,
            [selectedEmail.id!]: response.data.data || []
          }));
        }
      } catch (error: any) {
        // Silently handle 404s (email might not have labels yet)
        if (error?.response?.status !== 404) {
          console.error('Error loading labels for selected email:', error);
        }
      }
    };

    loadSelectedEmailLabels();
  }, [selectedEmail?.id]);

  // Load attachments for selected email
  useEffect(() => {
    if (!selectedEmail?.id) {
      setEmailAttachments([]);
      return;
    }

    const loadAttachments = async () => {
      setEmailAttachmentsLoading(true);
      try {
        const response = await api.get(`/email-client/email/${selectedEmail.id}/attachments`);
        if (response.data?.success) {
          setEmailAttachments(response.data.data || []);
        } else {
          setEmailAttachments([]);
        }
      } catch (error) {
        console.error('Error loading email attachments:', error);
        setEmailAttachments([]);
      } finally {
        setEmailAttachmentsLoading(false);
      }
    };

    loadAttachments();
  }, [selectedEmail?.id]);

  // Close label menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (labelMenuRef.current && !labelMenuRef.current.contains(event.target as Node)) {
        setShowLabelMenuInList(null);
        setShowLabelMenuInDetail(false);
      }
    };

    if (showLabelMenuInList !== null || showLabelMenuInDetail) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showLabelMenuInList, showLabelMenuInDetail]);

  // Add label to email
  const addLabelToEmail = async (emailId: number, labelId: number) => {
    try {
      console.log('[EmailClient] Adding label', labelId, 'to email', emailId);
      const response = await api.post(`/email-client-advanced/emails/${emailId}/labels`, { label_id: labelId });
      console.log('[EmailClient] Add label response:', response.data);
      
      if (response.data?.success) {
        toast.success('Label hinzugefügt');
        // Refresh labels for this email immediately
        try {
          const labelsResponse = await api.get(`/email-client-advanced/emails/${emailId}/labels`);
          console.log('[EmailClient] Refreshed labels:', labelsResponse.data);
          if (labelsResponse.data?.success) {
            const updatedLabels = labelsResponse.data.data || [];
            setEmailLabels(prev => ({
              ...prev,
              [emailId]: updatedLabels
            }));
            console.log('[EmailClient] Updated emailLabels state for email', emailId, ':', updatedLabels);
            
            // Also update the selectedEmail if it's the same email
            if (selectedEmail?.id === emailId) {
              setSelectedEmail({
                ...selectedEmail,
                labels: updatedLabels
              });
            }
          }
        } catch (refreshError) {
          console.error('Error refreshing labels:', refreshError);
        }
      } else {
        console.warn('[EmailClient] Add label response was not successful:', response.data);
      }
    } catch (error: any) {
      console.error('Error adding label:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Fehler beim Hinzufügen des Labels: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
    }
  };

  // Delete email
  const handleDeleteEmail = async (email: Email) => {
    if (!confirm('Möchten Sie diese E-Mail wirklich löschen?')) {
      return;
    }
    
    try {
      const box = actualMailbox || mailbox || 'INBOX';
      const response = await api.delete(`/email-client/email/${email.uid}?box=${encodeURIComponent(box)}`);
      
      if (response.data?.success) {
        toast.success('E-Mail gelöscht');
        // Remove from local state immediately
        setEmails(prev => prev.filter(e => e.uid !== email.uid));
        if (selectedEmail?.uid === email.uid) {
          setSelectedEmail(null);
        }
        // Also invalidate queries to refresh from server
        queryClient.invalidateQueries(['email-client', 'emails']);
        queryClient.invalidateQueries(['emails']);
      } else {
        toast.error('Fehler beim Löschen der E-Mail');
      }
    } catch (error: any) {
      console.error('Error deleting email:', error);
      toast.error(error?.response?.data?.message || 'Fehler beim Löschen der E-Mail');
    }
  };

  // Remove label from email
  const removeLabelFromEmail = async (emailId: number, labelId: number) => {
    try {
      console.log('[EmailClient] Removing label', labelId, 'from email', emailId);
      const response = await api.delete(`/email-client-advanced/emails/${emailId}/labels/${labelId}`);
      console.log('[EmailClient] Remove label response:', response.data);
      
      if (response.data?.success) {
        toast.success('Label entfernt');
        // Refresh labels for this email immediately
        try {
          const labelsResponse = await api.get(`/email-client-advanced/emails/${emailId}/labels`);
          console.log('[EmailClient] Refreshed labels after removal:', labelsResponse.data);
          if (labelsResponse.data?.success) {
            const updatedLabels = labelsResponse.data.data || [];
            setEmailLabels(prev => ({
              ...prev,
              [emailId]: updatedLabels
            }));
            console.log('[EmailClient] Updated emailLabels state for email', emailId, ':', updatedLabels);
            
            // Also update the selectedEmail if it's the same email
            if (selectedEmail?.id === emailId) {
              setSelectedEmail({
                ...selectedEmail,
                labels: updatedLabels
              });
            }
          }
        } catch (refreshError) {
          console.error('Error refreshing labels:', refreshError);
        }
      } else {
        console.warn('[EmailClient] Remove label response was not successful:', response.data);
      }
    } catch (error: any) {
      console.error('Error removing label:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Fehler beim Entfernen des Labels: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
    }
  };

  // Filter and sort emails
  const filteredAndSortedEmails = useMemo(() => {
    let filtered = emails;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(email => {
        const headers = email.headers || {};
        const subject = (headers.subject || '').toLowerCase();
        const from = (headers.from || '').toLowerCase();
        const to = (headers.to || '').toLowerCase();
        const body = (email.text || email.body || '').toLowerCase();
        
        return subject.includes(term) || from.includes(term) || to.includes(term) || body.includes(term);
      });
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
      }
      
      filtered = filtered.filter(email => {
        const emailDate = getEmailDate(email);
        return emailDate >= filterDate;
      });
    }

    // Apply read/unread/starred filter
    if (filter === 'unread') {
      filtered = filtered.filter(email => !isRead(email));
    } else if (filter === 'starred') {
      filtered = filtered.filter(email => isStarred(email));
    }

    // Sort emails
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'date':
          aValue = getEmailDate(a);
          bValue = getEmailDate(b);
          break;
        case 'from':
          aValue = getEmailFrom(a).toLowerCase();
          bValue = getEmailFrom(b).toLowerCase();
          break;
        case 'subject':
          aValue = getEmailSubject(a).toLowerCase();
          bValue = getEmailSubject(b).toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [emails, searchTerm, filter, dateFilter, sortBy, sortOrder]);

  // Detect mailboxes function
  const detectMailboxes = useCallback(async () => {
    // Prevent multiple simultaneous detections
    if (detectingMailboxes) return;
    
    setDetectingMailboxes(true);
    try {
      const response = await api.get('/email-client/detect-mailboxes');
      if (response.data?.success && response.data.data) {
        const detected = response.data.data;
        
        // No credentials: show friendly hint instead of error toasts
        if (detected.error && (String(detected.error).includes('credentials not found') || String(detected.error).includes('User email credentials not found'))) {
          setHasNoEmailCredentials(true);
          setDetectingMailboxes(false);
          return;
        }
        
        // Log detected mailboxes for debugging
        console.log('Detected mailboxes:', detected);
        
        setMailboxConfig({
          sent: detected.sent,
          drafts: detected.drafts,
          inbox: detected.inbox || 'INBOX'
        });
        
        // Only show toast if mailboxes were actually found (to avoid spam)
        const hadMailboxesBefore = mailboxConfig.sent || mailboxConfig.drafts;
        const hasMailboxesNow = detected.sent || detected.drafts;
        
        // Invalidate and refetch mailbox config to update cache
        queryClient.invalidateQueries(['email-client', 'mailbox-config']);
        
        // Only show success toast if we found new mailboxes that weren't there before
        if (hasMailboxesNow && !hadMailboxesBefore) {
          toast.success(`Mailboxen erkannt: ${detected.sent ? 'Gesendet' : ''} ${detected.drafts ? 'Entwürfe' : ''}`);
        } else if (!hasMailboxesNow && !hadMailboxesBefore) {
          // Only show warning if we still don't have mailboxes and didn't have them before
          toast('Keine Mailboxen gefunden. Bitte überprüfen Sie Ihre IMAP-Verbindung.');
        }
        // If we already had mailboxes, don't show any toast (silent update)
      } else {
        toast.error('Mailboxen konnten nicht erkannt werden');
      }
    } catch (error: any) {
      console.error('Error detecting mailboxes:', error);
      if (error.response?.data?.code === 'NO_EMAIL_CREDENTIALS') {
        setHasNoEmailCredentials(true);
        setDetectingMailboxes(false);
        return;
      }
      const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message || 'Unbekannter Fehler';
      const errorDetails = error.response?.data?.details;
      
      let userMessage = 'Fehler beim Erkennen der Mailboxen: ' + errorMsg;
      if (errorDetails && process.env.NODE_ENV === 'development') {
        userMessage += '\nDetails: ' + errorDetails;
      }
      
      toast.error(userMessage, { duration: 5000 });
    } finally {
      setDetectingMailboxes(false);
    }
  }, [detectingMailboxes, queryClient, mailboxConfig.sent, mailboxConfig.drafts]);

  // Load mailbox configuration with React Query (cached) – only when user has email credentials
  const { data: mailboxConfigData } = useQuery(
    ['email-client', 'mailbox-config'],
    async () => {
      const response = await api.get('/email-client/mailbox-config');
      return response.data?.data || { sent: null, drafts: null, inbox: 'INBOX' };
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      enabled: credentialsChecked && !hasNoEmailCredentials,
      onSuccess: (config) => {
        setMailboxConfig({
          sent: config.sent,
          drafts: config.drafts,
          inbox: config.inbox || 'INBOX'
        });
      }
    }
  );

  // Restore selectedEmailId from saved state on mount
  useEffect(() => {
    const s = loadEmailClientState();
    if (typeof s.selectedEmailId === 'number') {
      restoreSelectedEmailIdRef.current = s.selectedEmailId;
    }
  }, []);

  // When emails list loads: restore last selected email, or show newest so main view is never empty
  useEffect(() => {
    if (viewMode !== 'inbox' || showScheduledView || emails.length === 0) return;
    const idToRestore = restoreSelectedEmailIdRef.current;
    restoreSelectedEmailIdRef.current = null;
    const found = idToRestore != null ? emails.find((e) => e.id === idToRestore) : null;
    if (found) {
      setSelectedEmail(found);
    } else {
      // Keine gültige letzte Auswahl oder aktuelle Auswahl nicht mehr in der Liste → neueste anzeigen
      setSelectedEmail((current) => {
        if (current == null) return emails[0];
        const stillInList = emails.some((e) => e.id === current.id || e.uid === current.uid);
        return stillInList ? current : emails[0];
      });
    }
  }, [viewMode, showScheduledView, emails]);

  // Auto-load email body when selected email has no content (e.g. after session restore)
  useEffect(() => {
    if (!selectedEmail || selectedEmail.body || selectedEmail.html || selectedEmail.text) return;
    if (!selectedEmail.uid || !actualMailbox) return;
    setEmailBodyLoading(true);
    api.get(`/email-client/email/${selectedEmail.uid}/body`, { params: { box: actualMailbox } })
      .then((response) => {
        if (response.data?.success && response.data.data) {
          setSelectedEmail((prev) => prev && prev.uid === selectedEmail.uid ? {
            ...prev,
            body: response.data.data.body_text || '',
            html: response.data.data.body_html || '',
            text: response.data.data.body_text || ''
          } : prev);
        }
      })
      .catch((error) => {
        console.error('Error auto-loading email body:', error);
      })
      .finally(() => {
        setEmailBodyLoading(false);
      });
  }, [selectedEmail?.uid, actualMailbox]);

  // Persist view state and selected email so we can restore on next visit
  useEffect(() => {
    saveEmailClientState({
      viewMode,
      mailbox,
      showScheduledView,
      showElternbriefeView,
      selectedEmailId: selectedEmail?.id ?? null
    });
  }, [viewMode, mailbox, showScheduledView, showElternbriefeView, selectedEmail?.id]);

  // Update mailboxConfig when data changes
  useEffect(() => {
    if (mailboxConfigData) {
      console.log('[EmailClient] Updating mailboxConfig:', mailboxConfigData);
      setMailboxConfig({
        sent: mailboxConfigData.sent || null,
        drafts: mailboxConfigData.drafts || null,
        inbox: mailboxConfigData.inbox || 'INBOX'
      });
      
      // If no mailboxes detected, try to detect them ONCE (but don't block on error)
      // Only auto-detect if we have credentials, haven't tried yet and not currently detecting
      if (!hasNoEmailCredentials && !mailboxConfigData.sent && !mailboxConfigData.drafts && !detectingMailboxes && !hasTriedAutoDetect) {
        setHasTriedAutoDetect(true); // Mark as tried to prevent loop
        // Use setTimeout to avoid calling during render
        setTimeout(() => {
          detectMailboxes();
        }, 100);
      }
    }
  }, [mailboxConfigData, detectingMailboxes, hasTriedAutoDetect]);

  // Refetch emails when dependencies change
  useEffect(() => {
    if (viewMode === 'inbox' && actualMailbox) {
      setCurrentPage(1); // Reset to first page
      // Don't clear emails immediately – let keepPreviousData keep the list visible while the
      // fresh fetch is in-flight. Emails are replaced once onSuccess / the safety-net useEffect fires.
      queryClient.invalidateQueries(['email-client', 'emails']);
    } else if (viewMode === 'threads') {
      fetchThreads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, filter, dateFilter, actualMailbox, searchQuery]); // Reload when mailbox or filter changes

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (viewMode !== 'inbox' || !hasMore || loading || filteredAndSortedEmails.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreEmails();
        }
      },
      {
        root: null,
        rootMargin: '200px', // Start loading 200px before reaching the bottom
        threshold: 0.1
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, loading, viewMode, loadMoreEmails, filteredAndSortedEmails.length]);

  // Helper function to extract email addresses from emails
  const extractEmailAddresses = useCallback((emails: Email[]): string[] => {
    const emailAddresses = new Set<string>();
    const extractEmailFromString = (emailStr: string) => {
      if (!emailStr) return null;
      const emails = emailStr.split(',').map(e => e.trim());
      const addresses: string[] = [];
      for (const e of emails) {
        const emailMatch = e.match(/<(.+?)>/) || e.match(/([^\s<>]+@[^\s<>]+)/);
        if (emailMatch) {
          const emailAddr = (emailMatch[1] || emailMatch[0]).trim().toLowerCase();
          if (emailAddr && emailAddr.includes('@')) {
            addresses.push(emailAddr);
          }
        }
      }
      return addresses;
    };

    emails.forEach((email) => {
      const from = getEmailFrom(email);
      const fromAddresses = extractEmailFromString(from);
      if (fromAddresses) {
        fromAddresses.forEach(addr => emailAddresses.add(addr));
      }
      
      const to = getEmailTo(email);
      if (to) {
        const toAddresses = extractEmailFromString(to);
        if (toAddresses) {
          toAddresses.forEach(addr => emailAddresses.add(addr));
        }
      }
      
      if (email.headers?.cc) {
        const ccAddresses = extractEmailFromString(email.headers.cc);
        if (ccAddresses) {
          ccAddresses.forEach(addr => emailAddresses.add(addr));
        }
      }
    });

    return Array.from(emailAddresses);
  }, []);

  // Fetch contacts with React Query (cached for 10 minutes)
  const emailAddresses = useMemo(() => extractEmailAddresses(emails), [emails, extractEmailAddresses]);
  
  const { data: contactsData } = useQuery<Record<string, any>>(
    ['email-contacts', emailAddresses.sort().join(',')],
    async () => {
      if (emailAddresses.length === 0) return {};
      
      // Try to sync address book first (only once per session)
      const syncKey = 'addressBookSynced';
      if (!sessionStorage.getItem(syncKey)) {
        try {
          await api.post('/address-book/sync');
          sessionStorage.setItem(syncKey, 'true');
        } catch (syncError: any) {
          console.warn('[EmailClient] Address book sync failed:', syncError?.response?.data || syncError.message);
        }
      }

      const response = await api.get('/address-book/match-email', {
        params: { emails: emailAddresses.join(',') }
      });
      return response.data?.data || {};
    },
    {
      enabled: emailAddresses.length > 0,
      staleTime: 10 * 60 * 1000, // 10 minutes
      cacheTime: 60 * 60 * 1000, // 1 hour
      refetchOnWindowFocus: false,
      // Cache in localStorage as well
      onSuccess: (data) => {
        try {
          localStorage.setItem('email-contacts-cache', JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('[EmailClient] Failed to cache contacts in localStorage:', e);
        }
      },
    }
  );

  // Load contacts from cache on mount
  useEffect(() => {
    if (!contactsData && emailAddresses.length > 0) {
      try {
        const cached = localStorage.getItem('email-contacts-cache');
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 1 hour old
          if (Date.now() - timestamp < 60 * 60 * 1000) {
            setEmailContacts(data);
            console.log('[EmailClient] Loaded contacts from localStorage cache');
          }
        }
      } catch (e) {
        console.warn('[EmailClient] Failed to load contacts from localStorage:', e);
      }
    } else if (contactsData) {
      setEmailContacts(contactsData);
    }
  }, [contactsData, emailAddresses.length]);

  // Force-refresh contacts: clear all caches and re-sync address book
  const [contactsRefreshing, setContactsRefreshing] = useState(false);
  const refreshContacts = async () => {
    setContactsRefreshing(true);
    try {
      sessionStorage.removeItem('addressBookSynced');
      localStorage.removeItem('email-contacts-cache');
      queryClient.removeQueries(['email-contacts']);
      await api.post('/address-book/sync');
      sessionStorage.setItem('addressBookSynced', 'true');
      await queryClient.invalidateQueries(['email-contacts']);
    } catch (e) {
      console.warn('[EmailClient] Contact refresh failed:', e);
    } finally {
      setContactsRefreshing(false);
    }
  };

  // Fetch student photos with React Query (cached)
  const studentUnids = useMemo(() => {
    return Object.values(emailContacts)
      .filter((contact: any) => contact.contact_type === 'student' && contact.reference_id)
      .map((contact: any) => contact.reference_id) as string[];
  }, [emailContacts]);

  const { data: photosData } = useQuery<Record<string, string>>(
    ['student-photos', studentUnids.sort().join(',')],
    async () => {
      if (studentUnids.length === 0) return {};
      
      const photos = await loadStudentPhotos(studentUnids);
      const photoUrls: Record<string, string> = {};
      
      studentUnids.forEach((unid: string) => {
        const photoBase64 = photos[unid];
        if (hasPhoto(photoBase64)) {
          photoUrls[unid] = createPhotoDataUrl(photoBase64!);
        }
      });
      
      return photoUrls;
    },
    {
      enabled: studentUnids.length > 0,
      staleTime: 30 * 60 * 1000, // 30 minutes - photos don't change often
      cacheTime: 24 * 60 * 60 * 1000, // 24 hours
      refetchOnWindowFocus: false,
      // Cache in localStorage as well
      onSuccess: (data) => {
        try {
          localStorage.setItem('student-photos-cache', JSON.stringify({
            data,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('[EmailClient] Failed to cache photos in localStorage:', e);
        }
      },
    }
  );

  // Load photos from cache on mount
  useEffect(() => {
    if (!photosData && studentUnids.length > 0) {
      try {
        const cached = localStorage.getItem('student-photos-cache');
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 24 hours old
          if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
            setStudentPhotos(data);
            console.log('[EmailClient] Loaded photos from localStorage cache');
          }
        }
      } catch (e) {
        console.warn('[EmailClient] Failed to load photos from localStorage:', e);
      }
    } else if (photosData) {
      setStudentPhotos(photosData);
    }
  }, [photosData, studentUnids.length]);

  // Load student classes with React Query (cached)
  const { data: classesData } = useQuery<Record<string, string>>(
    ['student-classes', studentUnids.sort().join(',')],
    async () => {
      if (studentUnids.length === 0) return {};
      
      const classes: Record<string, string> = {};
      const classPromises = studentUnids.map(async (unid: string) => {
        try {
          const response = await api.get(`/students/unid/${unid}`);
          if (response.data?.Klasse) {
            classes[unid] = response.data.Klasse;
          }
        } catch (err) {
          // Ignore errors for individual students
        }
      });
      await Promise.all(classPromises);
      return classes;
    },
    {
      enabled: studentUnids.length > 0,
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 60 * 60 * 1000, // 1 hour
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (classesData) {
      setStudentClasses(classesData);
    }
  }, [classesData]);

  // Check linked students for emails (batch request)
  useEffect(() => {
    const checkLinkedStudents = async () => {
      const emailIds = emails.filter(e => e.id).map(e => e.id!);
      if (emailIds.length === 0) return;

      try {
        // Use batch endpoint to check all emails at once
        const response = await api.post('/email-client/emails/linked-students', { emailIds });
        if (response.data?.success && response.data.data) {
          setLinkedStudents(response.data.data);
        }
      } catch (error) {
        console.error('Error checking linked students:', error);
        // Fallback: set empty object on error
        setLinkedStudents({});
      }
    };

    if (emails.length > 0) {
      checkLinkedStudents();
    }
  }, [emails]);

  // Add email to student file
  const addEmailToStudentFile = async (email: Email, studentUnids: string[]) => {
    if (!email.id) {
      toast.error('E-Mail-ID fehlt');
      return;
    }

    try {
      const response = await api.post(`/email-client/email/${email.id}/add-to-student-file`, {
        studentUnids
      });

      if (response.data?.success) {
        const results = response.data.data;
        const successCount = results.filter((r: any) => r.success).length;
        const errorCount = results.filter((r: any) => !r.success).length;

        if (successCount > 0) {
          toast.success(`${successCount} E-Mail${successCount > 1 ? 's' : ''} zu Schülerakte${successCount > 1 ? 'n' : ''} hinzugefügt`);
          // Refresh linked students
          const linkedResponse = await api.get(`/email-client/email/${email.id}/linked-students`);
          if (linkedResponse.data?.success) {
            setLinkedStudents(prev => ({
              ...prev,
              [email.id!]: linkedResponse.data.data
            }));
          }
        }
        if (errorCount > 0) {
          const errors = results.filter((r: any) => !r.success);
          const alreadyLinked = errors.filter((e: any) => e.error === 'Already linked').length;
          if (alreadyLinked > 0) {
            toast.error(`${alreadyLinked} E-Mail${alreadyLinked > 1 ? 's' : ''} bereits verknüpft`);
          } else {
            toast.error(`${errorCount} Fehler beim Hinzufügen`);
          }
        }
      }
    } catch (error: any) {
      console.error('Error adding email to student file:', error);
      toast.error('Fehler beim Hinzufügen zur Schülerakte: ' + (error.response?.data?.error || error.message));
    }
  };

  const removeEmailStudentLink = async (email: Email, fileId: string) => {
    if (!email.id) return;
    try {
      await api.delete(`/email-client/email/${email.id}/student-link/${fileId}`);
      toast.success('Verknüpfung entfernt');
      const linkedResponse = await api.get(`/email-client/email/${email.id}/linked-students`);
      if (linkedResponse.data?.success) {
        setLinkedStudents(prev => ({
          ...prev,
          [email.id!]: linkedResponse.data.data
        }));
      }
    } catch (error: any) {
      console.error('Error removing student link:', error);
      toast.error('Fehler beim Entfernen der Verknüpfung: ' + (error.response?.data?.error || error.message));
    }
  };

  const tabInactive =
    'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800';
  const tabActiveBlue =
    'bg-blue-50 dark:bg-blue-950/40 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400';
  const tabActivePurple =
    'bg-purple-50 dark:bg-purple-950/40 border-purple-600 dark:border-purple-400 text-purple-600 dark:text-purple-400';

  return (
    <div className="py-8">
        {/* Freundlicher Hinweis, wenn noch kein E-Mail-Konto hinterlegt ist */}
        {credentialsChecked && hasNoEmailCredentials && (
          <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-5 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg shrink-0">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-50">Noch kein E-Mail-Konto eingerichtet</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Um E-Mails zu lesen und zu senden, richten Sie bitte in den Einstellungen ein E-Mail-Konto ein (E-Mail-Signatur / E-Mail-Konten).
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/settings?tab=email-signature')}
              className="btn btn-primary shrink-0 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Zu den Einstellungen
            </button>
          </div>
        )}

        {/* Header */}
        <div className="card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50">E-Mail-Client</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Verwalten Sie Ihre E-Mails</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEditingDraft(null);
                  setReplyMode(undefined);
                  setOriginalEmail(undefined);
                  setShowComposeModal(true);
                }}
                className="btn btn-primary btn-sm flex items-center"
                title="E-Mail verfassen"
              >
                <Send className="w-4 h-4 mr-1" />
                Verfassen
              </button>
              <button
                onClick={() => {
                  if (viewMode === 'inbox') {
                    queryClient.invalidateQueries(['email-client', 'emails']);
                    refetchEmails();
                  } else {
                    fetchThreads();
                  }
                }}
                disabled={loading}
                className="btn btn-secondary btn-sm flex items-center"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </button>
              <button
                onClick={refreshContacts}
                disabled={contactsRefreshing}
                className="btn btn-secondary btn-sm flex items-center"
                title="Kontakte neu synchronisieren (bei geänderten E-Mail-Adressen)"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${contactsRefreshing ? 'animate-spin' : ''}`} />
                Kontakte
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 pointer-events-none" />
              <input
                type="search"
                placeholder="E-Mails durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                className="input-field min-h-[2.25rem] py-2 text-sm pl-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value as 'all' | 'unread' | 'starred');
                  setCurrentPage(1);
                }}
                className="input-field min-h-[2.25rem] w-auto min-w-[6.5rem] py-2 text-sm"
              >
                <option value="all">Alle</option>
                <option value="unread">Ungelesen</option>
                <option value="starred">Markiert</option>
              </select>
              <select
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value as 'all' | 'today' | 'week' | 'month');
                  setCurrentPage(1);
                }}
                className="input-field min-h-[2.25rem] w-auto min-w-[6.5rem] py-2 text-sm"
              >
                <option value="all">Alle Daten</option>
                <option value="today">Heute</option>
                <option value="week">Letzte 7 Tage</option>
                <option value="month">Letzter Monat</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value as 'date' | 'from' | 'subject');
                }}
                className="input-field min-h-[2.25rem] w-auto min-w-[6.5rem] py-2 text-sm"
              >
                <option value="date">Datum</option>
                <option value="from">Absender</option>
                <option value="subject">Betreff</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="btn btn-outline btn-sm flex items-center"
                title={sortOrder === 'asc' ? 'Aufsteigend sortieren' : 'Absteigend sortieren'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {/* Mailbox Selection */}
          <div className="flex gap-2 mt-4 border-b border-gray-200 dark:border-gray-700 items-center">
            <button
              onClick={() => {
                setShowScheduledView(false);
                setShowElternbriefeView(false);
                setMailbox(mailboxConfig.inbox);
                setSelectedEmail(null);
                setSelectedThread(null);
                setViewMode('inbox');
              }}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors border-b-2 ${
                !showScheduledView && !showElternbriefeView && mailbox === mailboxConfig.inbox
                  ? tabActiveBlue
                  : tabInactive
              }`}
            >
              <Inbox className="w-4 h-4 inline mr-2" />
              Posteingang
            </button>
            {mailboxConfig.sent && (
              <button
                onClick={() => {
                  setShowScheduledView(false);
                  setShowElternbriefeView(false);
                  setMailbox(mailboxConfig.sent!);
                  setSelectedEmail(null);
                  setSelectedThread(null);
                  setViewMode('inbox');
                }}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors border-b-2 ${
                  !showScheduledView && !showElternbriefeView && mailbox === mailboxConfig.sent
                    ? tabActiveBlue
                    : tabInactive
                }`}
              >
                <Send className="w-4 h-4 inline mr-2" />
                Gesendet
              </button>
            )}
            {mailboxConfig.drafts && (
              <button
                onClick={() => {
                  setShowScheduledView(false);
                  setShowElternbriefeView(false);
                  setMailbox(mailboxConfig.drafts!);
                  setSelectedEmail(null);
                  setSelectedThread(null);
                  setViewMode('inbox');
                }}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors border-b-2 ${
                  !showScheduledView && !showElternbriefeView && mailbox === mailboxConfig.drafts
                    ? tabActiveBlue
                    : tabInactive
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Entwürfe
              </button>
            )}
            <button
              onClick={() => {
                setShowScheduledView(true);
                setShowElternbriefeView(false);
                setSelectedEmail(null);
                setSelectedThread(null);
              }}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors border-b-2 ${
                showScheduledView
                  ? tabActivePurple
                  : tabInactive
              }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Geplant
            </button>
            <button
              onClick={() => {
                setShowScheduledView(false);
                setShowElternbriefeView(true);
                setSelectedEmail(null);
                setSelectedThread(null);
              }}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors border-b-2 ${
                showElternbriefeView
                  ? tabActiveBlue
                  : tabInactive
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Elternbriefe
              {newResponsesCount > 0 && (
                <span
                  className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold text-white bg-blue-600 rounded-full"
                  title={`${newResponsesCount} neue Rückmeldung${newResponsesCount !== 1 ? 'en' : ''} von Eltern (letzte 7 Tage). Durch Anklicken und Betrachten des jeweiligen Elternbriefs verschwindet er aus dem Badge.`}
                >
                  {newResponsesCount > 99 ? '99+' : newResponsesCount}
                </span>
              )}
            </button>
            <button
              onClick={() => {
                setShowScheduledView(false);
                setShowElternbriefeView(false);
                setViewMode('threads');
                setSelectedEmail(null);
                setSelectedThread(null);
              }}
              className={`px-4 py-2 rounded-t-lg font-medium transition-colors border-b-2 ml-auto ${
                viewMode === 'threads'
                  ? tabActiveBlue
                  : tabInactive
              }`}
            >
              <Folder className="w-4 h-4 inline mr-2" />
              Threads
            </button>
            <button
              onClick={detectMailboxes}
              disabled={detectingMailboxes}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors flex items-center gap-1"
              title="Mailboxen neu erkennen"
            >
              <RefreshCw className={`w-4 h-4 ${detectingMailboxes ? 'animate-spin' : ''}`} />
              {detectingMailboxes ? 'Erkenne...' : 'Erkennen'}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {showElternbriefeView ? (
            <Massenmail
              embedded
              onRefresh={() => {
                queryClient.invalidateQueries(['massenmail', 'new-responses-count']);
                refetchNewResponses();
              }}
            />
          ) : !showScheduledView && viewMode === 'inbox' && (credentialsLoading || (loading && emails.length === 0)) ? (
            <div className="col-span-1 lg:col-span-3 flex flex-col items-center justify-center py-16 px-4 card">
              <RefreshCw className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-400 mb-4" />
              <p className="text-gray-700 dark:text-gray-300 font-medium">
                {credentialsLoading ? 'E-Mail wird initialisiert…' : 'E-Mail-Ansicht wird geladen…'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Bitte einen Moment Geduld.</p>
              {!credentialsLoading && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 max-w-sm text-center">
                  Beim ersten Mal oder bei neuer E-Mail-Adresse kann der Abgleich etwas länger dauern (bis zu 1 Minute).
                </p>
              )}
            </div>
          ) : (
            <>
          {/* Email List / Scheduled List */}
          <div className={`lg:col-span-1 ${selectedEmail || selectedThread ? 'hidden lg:block' : 'lg:col-span-1'}`}>
            <div className="card">
              {showScheduledView ? (
                <>
                  {scheduledLoading ? (
                    <div className="p-8 text-center">
                      <RefreshCw className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Geplante E-Mails werden geladen...</p>
                    </div>
                  ) : scheduledEmails.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                      <p>Keine geplanten E-Mails</p>
                      <p className="text-sm mt-2">Nutzen Sie „Später senden“ beim Verfassen, um E-Mails zu planen.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[calc(100vh-300px)] overflow-y-auto">
                      {scheduledEmails.map((s: { id: number; subject: string; to: string; studentUnid: string | null; scheduledAt: string }) => (
                        <div
                          key={s.id}
                          className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 dark:text-gray-50 truncate">{s.subject || '(Kein Betreff)'}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">An: {s.to || s.studentUnid || '—'}</p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                {new Date(s.scheduledAt).toLocaleString('de-DE', {
                                  dateStyle: 'short',
                                  timeStyle: 'short'
                                })}
                              </p>
                            </div>
                            <button
                              onClick={() => cancelScheduledEmail(s.id)}
                              className="flex-shrink-0 px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Planung abbrechen"
                            >
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : loading && emails.length === 0 ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">E-Mails werden geladen...</p>
                </div>
              ) : viewMode === 'inbox' ? (
                <>
                  {filteredAndSortedEmails.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      <Mail className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                      <p>Keine E-Mails gefunden</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[calc(100vh-300px)] overflow-y-auto">
                      {filteredAndSortedEmails.map((email) => {
                        // Extract contact info from FROM field
                        const from = getEmailFrom(email);
                        const fromEmailMatch = from.match(/<(.+?)>/) || from.match(/([^\s<>]+@[^\s<>]+)/);
                        const fromEmailAddress = fromEmailMatch ? (fromEmailMatch[1] || fromEmailMatch[0]).trim().toLowerCase() : '';
                        
                        // Also check TO field for student contacts (parent emails)
                        const to = getEmailTo(email);
                        const toEmailMatch = to ? (to.match(/<(.+?)>/) || to.match(/([^\s<>]+@[^\s<>]+)/)) : null;
                        const toEmailAddress = toEmailMatch ? (toEmailMatch[1] || toEmailMatch[0]).trim().toLowerCase() : '';
                        
                        // Check CC field as well
                        const cc = email.headers?.cc || '';
                        const ccEmailMatch = cc ? (cc.match(/<(.+?)>/) || cc.match(/([^\s<>]+@[^\s<>]+)/)) : null;
                        const ccEmailAddress = ccEmailMatch ? (ccEmailMatch[1] || ccEmailMatch[0]).trim().toLowerCase() : '';
                        
                        // Try to find student contact in FROM, TO, or CC (prioritize student contacts)
                        let contact = fromEmailAddress ? emailContacts[fromEmailAddress] : null;
                        if (!contact || contact.contact_type !== 'student') {
                          contact = toEmailAddress ? emailContacts[toEmailAddress] : null;
                        }
                        if (!contact || contact.contact_type !== 'student') {
                          contact = ccEmailAddress ? emailContacts[ccEmailAddress] : null;
                        }
                        // If still no student contact found, use FROM contact for display
                        if (!contact && fromEmailAddress) {
                          contact = emailContacts[fromEmailAddress];
                        }
                        
                        const displayName = contact?.name || (from.includes('<') ? from.split('<')[0].trim() || from : from);
                        const photoUrl = contact?.contact_type === 'student' && contact.reference_id 
                          ? studentPhotos[contact.reference_id] 
                          : null;
                        
                        // Extract student name (context)
                        const studentName = extractStudentName(email, contact);
                        
                        // Get meaningful subject
                        const meaningfulSubject = getMeaningfulSubject(email);
                        
                        return (
                          <div
                            key={email.uid}
                            onDoubleClick={() => {
                              if (isDraftsFolder) openDraftForEditing(email);
                            }}
                            onClick={async () => {
                              setSelectedEmail(email);
                              // Load email body if not already loaded
                              if (!email.body && !email.html && !email.text) {
                                setEmailBodyLoading(true);
                                try {
                                  const response = await api.get(`/email-client/email/${email.uid}/body`, {
                                    params: { box: actualMailbox }
                                  });
                                  if (response.data?.success && response.data.data) {
                                    setSelectedEmail({
                                      ...email,
                                      body: response.data.data.body_text || '',
                                      html: response.data.data.body_html || '',
                                      text: response.data.data.body_text || ''
                                    });
                                  }
                                } catch (error: any) {
                                  console.error('Error loading email body:', error);
                                  toast.error('Fehler beim Laden des E-Mail-Inhalts');
                                } finally {
                                  setEmailBodyLoading(false);
                                }
                              }
                            }}
                            className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800 ${
                              selectedEmail?.uid === email.uid ? 'bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-600 dark:border-blue-400' : ''
                            } ${!isRead(email) ? 'bg-blue-50/30 dark:bg-blue-950/20' : ''}`}
                          >
                            {/* Chips: Labels und verknüpfte Schüler gleichberechtigt in einer Zeile */}
                            {((email.id && emailLabels[email.id] && emailLabels[email.id].length > 0) ||
                              (email.id && linkedStudents[email.id] && linkedStudents[email.id].length > 0) ||
                              (studentName && contact?.reference_id)) && (
                              <div className="flex flex-wrap gap-2 mb-3">
                                {/* Label-Chips */}
                                {email.id && emailLabels[email.id]?.map((label: EmailLabel) => (
                                  <span
                                    key={`label-${label.id}`}
                                    className="email-category-chip cursor-pointer hover:opacity-90 group"
                                    style={emailLabelChipStyle(label.color)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (email.id) removeLabelFromEmail(email.id, label.id);
                                    }}
                                    title="Klicken zum Entfernen"
                                  >
                                    <Tag className="w-3.5 h-3.5 shrink-0" />
                                    {label.name}
                                    <X className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                  </span>
                                ))}
                                {/* Schüler-Chips (verknüpfte + Absender/Empfänger) – gleicher Stil wie Labels */}
                                {email.id && linkedStudents[email.id]?.map((linked) => (
                                  <span
                                    key={`linked-${linked.sophomorix_unid}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-100 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200"
                                    title={`Verknüpft mit Schülerakte: ${linked.student_name}`}
                                  >
                                    <User className="w-3.5 h-3.5 shrink-0" />
                                    {linked.student_name}
                                    {linked.klasse && <span className="text-blue-600/90 dark:text-blue-300">({linked.klasse})</span>}
                                  </span>
                                ))}
                                {studentName && contact?.reference_id && (!email.id || !linkedStudents[email.id]?.some(l => l.sophomorix_unid === contact?.reference_id)) && (
                                  <span
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-600 text-slate-800 dark:text-gray-200"
                                    title="Aus Absender/Empfänger"
                                  >
                                    <User className="w-3.5 h-3.5 shrink-0" />
                                    {studentName}
                                    {studentClasses[contact.reference_id] && (
                                      <span className="text-slate-600 dark:text-slate-400">({studentClasses[contact.reference_id]})</span>
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-start gap-3">
                              {/* Sidebar: Aktionen + Zeit – klar abgegrenzt, Buttons sichtbar */}
                              <div className="flex flex-col items-stretch gap-1.5 flex-shrink-0 min-w-[5rem] py-1.5 px-2 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700">
                                {/* Gelesen / Ungelesen */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleReadStatus(email, !isRead(email));
                                  }}
                                  className={`email-list-action-btn ${
                                    isRead(email) ? 'email-list-action-btn--read' : 'email-list-action-btn--unread'
                                  }`}
                                  title={isRead(email) ? 'Als ungelesen markieren' : 'Als gelesen markieren'}
                                >
                                  {isRead(email) ? (
                                    <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                                  ) : (
                                    <Circle className="w-3.5 h-3.5 shrink-0" />
                                  )}
                                  {isRead(email) ? 'Gelesen' : 'Ungelesen'}
                                </button>
                                
                                {/* Label */}
                                {email.id && (
                                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowLabelMenuInDetail(false);
                                        setShowLabelMenuInList(showLabelMenuInList === email.id ? null : email.id!);
                                      }}
                                      className="email-list-action-btn"
                                      title="Label: E-Mail kategorisieren"
                                    >
                                      <Tag className="w-3.5 h-3.5 shrink-0" />
                                      Label
                                    </button>
                                    {showLabelMenuInList === email.id && (
                                      <div
                                        ref={labelMenuRef}
                                        className="email-floating-menu left-0 top-full mt-1 min-w-[200px] max-h-60 overflow-y-auto"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Labels hinzufügen</div>
                                        {labels.length === 0 ? (
                                          <div className="px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                                            Keine Labels verfügbar. Bitte erstellen Sie zuerst Labels in den Einstellungen.
                                          </div>
                                        ) : (
                                          labels.map((label) => {
                                            const isAssigned = email.id && emailLabels[email.id]?.some(l => l.id === label.id);
                                            return (
                                              <button
                                                key={label.id}
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  if (email.id) {
                                                    if (isAssigned) removeLabelFromEmail(email.id, label.id);
                                                    else addLabelToEmail(email.id, label.id);
                                                    setTimeout(() => {
                                                      setShowLabelMenuInList(null);
                                                      setShowLabelMenuInDetail(false);
                                                    }, 100);
                                                  }
                                                }}
                                                className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 ${isAssigned ? 'bg-gray-50 dark:bg-gray-800/60' : ''}`}
                                              >
                                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: label.color || '#6B7280' }} />
                                                <span>{label.name}</span>
                                                {isAssigned && <X className="w-3 h-3 ml-auto" />}
                                              </button>
                                            );
                                          })
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Akte */}
                                {email.id && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const linked = linkedStudents[email.id!] || [];
                                      const studentUnid = contact?.contact_type === 'student' && contact.reference_id ? contact.reference_id : null;
                                      if (studentUnid && linked.length === 0) {
                                        addEmailToStudentFile(email, [studentUnid]);
                                      } else {
                                        setEmailForStudentSelect(email);
                                        setSelectedStudentsForFile(linked.map(l => l.sophomorix_unid));
                                        setShowStudentSelectModal(true);
                                      }
                                    }}
                                    className={`email-list-action-btn ${
                                      linkedStudents[email.id!] && linkedStudents[email.id!].length > 0
                                        ? 'email-list-action-btn--linked'
                                        : ''
                                    }`}
                                    title={
                                      linkedStudents[email.id!] && linkedStudents[email.id!].length > 0
                                        ? `Akte: ${linkedStudents[email.id!].length} verknüpft (Klicken = verwalten)`
                                        : 'E-Mail in Schülerakte ablegen'
                                    }
                                  >
                                    <FolderOpen className={`w-3.5 h-3.5 shrink-0 ${linkedStudents[email.id!]?.length ? 'fill-current' : ''}`} />
                                    Akte
                                  </button>
                                )}
                                
                                <div className="border-t border-gray-200 dark:border-gray-700 my-0.5 w-full" aria-hidden />
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 text-center leading-tight">
                                  {formatRelativeTime(getEmailDate(email))}
                                </span>
                              </div>
                              
                              <div className="flex-1 min-w-0 space-y-1">
                                {/* Betreff – deutlich hervorgehoben */}
                                <p className={`text-base font-semibold leading-snug ${!isRead(email) ? 'text-gray-900 dark:text-gray-50' : 'text-gray-800 dark:text-gray-200'}`}>
                                  {meaningfulSubject}
                                </p>
                                
                                {/* LLM-Abstract – klar sichtbar, eigene Zeile */}
                                {email.llm_summary && meaningfulSubject !== email.llm_summary && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug line-clamp-2">
                                    {email.llm_summary}
                                  </p>
                                )}
                                
                                {/* Originaltext anreißen, wenn Platz und Inhalt vorhanden */}
                                {(() => {
                                  const snippet = getBodySnippet(email);
                                  const showSnippet = snippet && snippet !== email.llm_summary && meaningfulSubject !== snippet;
                                  return showSnippet ? (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                                      {snippet}
                                    </p>
                                  ) : null;
                                })()}
                                
                                {/* Absender + Icons */}
                                <div className="flex items-center justify-between mt-2 gap-2 pt-0.5">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    {/* 5. ABSENDERNAME - Nur einmal, dezent, nicht wenn Schüler */}
                                    {(!studentName || contact?.contact_type !== 'student') && (
                                      <div className="flex items-center gap-1.5">
                                        {photoUrl && !studentName ? (
                                          <div className="relative group">
                                            <img 
                                              src={photoUrl} 
                                              alt={displayName}
                                              className="w-4 h-4 rounded-full object-cover flex-shrink-0 border border-gray-200 dark:border-gray-700 transition-transform duration-200 hover:scale-[3] hover:z-50 hover:shadow-2xl cursor-pointer"
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                              }}
                                            />
                                          </div>
                                        ) : (
                                          <div className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                                            <User className="w-2.5 h-2.5 text-gray-400 dark:text-gray-500" />
                                          </div>
                                        )}
                                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                          {displayName}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Icons rechts */}
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {isStarred(email) && (
                                      <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                                    )}
                                    {email.attachments.length > 0 && (
                                      <Paperclip className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Load More Trigger */}
                      {hasMore && (
                        <div ref={loadMoreRef} className="p-4 text-center">
                          {loading ? (
                            <div className="flex items-center justify-center">
                              <RefreshCw className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400 mr-2" />
                              <span className="text-sm text-gray-600 dark:text-gray-400">Lade weitere E-Mails...</span>
                            </div>
                          ) : (
                            <div className="h-4" /> // Invisible trigger element
                          )}
                        </div>
                      )}
                      
                      {!hasMore && filteredAndSortedEmails.length > 0 && (
                        <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                          Alle E-Mails geladen
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {viewMode === 'threads' && loading ? (
                    <div className="p-8 text-center">
                      <RefreshCw className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Threads werden geladen…</p>
                    </div>
                  ) : threads.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                      <Folder className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                      <p>Keine Threads gefunden</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[calc(100vh-300px)] overflow-y-auto">
                      {threads.map((thread) => (
                        <div
                          key={thread.threadId}
                          className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                            selectedThread?.threadId === thread.threadId ? 'bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-600 dark:border-blue-400' : ''
                          }`}
                        >
                          <div
                            onClick={() => {
                              if (expandedThreads.has(thread.threadId)) {
                                setExpandedThreads(prev => {
                                  const next = new Set(prev);
                                  next.delete(thread.threadId);
                                  return next;
                                });
                              } else {
                                setExpandedThreads(prev => new Set(prev).add(thread.threadId));
                              }
                            }}
                            className="flex items-center justify-between mb-2"
                          >
                            <div className="flex items-center gap-2 flex-1">
                              {expandedThreads.has(thread.threadId) ? (
                                <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              )}
                              <p className="text-sm font-medium">{decodeRFC2047(thread.subject || '(Kein Betreff)')}</p>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatRelativeTime(thread.lastActivity)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                            {thread.emails.length} {thread.emails.length === 1 ? 'E-Mail' : 'E-Mails'}
                          </p>
                          {expandedThreads.has(thread.threadId) && (
                            <div className="mt-2 ml-6 space-y-1">
                              {thread.emails.map((email, idx) => (
                                <div
                                  key={email.uid}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedThread(thread);
                                    setSelectedEmail(email);
                                  }}
                                  className={`p-2 rounded text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${
                                    selectedEmail?.uid === email.uid ? 'bg-blue-100 dark:bg-blue-950/50' : ''
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="truncate">{(() => {
                                      const from = getEmailFrom(email);
                                      return from.includes('<') ? from.split('<')[0].trim() : from;
                                    })()}</span>
                                    <span className="text-gray-400 dark:text-gray-500 ml-2">{formatRelativeTime(getEmailDate(email))}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Email Detail View */}
          {(selectedEmail || selectedThread) && (
            <div className="lg:col-span-2">
              <div className="card">
                {selectedEmail && (
                  <div className="p-6">
                    {/* Email Header */}
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                      <div className="flex items-start justify-between mb-2">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50">
                          {getEmailSubject(selectedEmail)}
                        </h2>
                        <div className="flex items-center gap-2">
                          {isDraftsFolder && (
                            <button
                              onClick={() => openDraftForEditing(selectedEmail)}
                              className="btn btn-primary btn-sm flex items-center mr-1"
                              title="Entwurf weiter bearbeiten"
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Weiter bearbeiten
                            </button>
                          )}
                          {/* Add to Student File Button */}
                          {selectedEmail.id && (
                            <button
                              onClick={() => {
                                const linked = linkedStudents[selectedEmail.id!] || [];
                                const from = getEmailFrom(selectedEmail);
                                const fromEmailMatch = from.match(/<(.+?)>/) || from.match(/([^\s<>]+@[^\s<>]+)/);
                                const fromEmailAddress = fromEmailMatch ? (fromEmailMatch[1] || fromEmailMatch[0]).trim().toLowerCase() : '';
                                
                                // Also check TO and CC fields for student contacts
                                const to = getEmailTo(selectedEmail);
                                const toEmailMatch = to ? (to.match(/<(.+?)>/) || to.match(/([^\s<>]+@[^\s<>]+)/)) : null;
                                const toEmailAddress = toEmailMatch ? (toEmailMatch[1] || toEmailMatch[0]).trim().toLowerCase() : '';
                                
                                const cc = selectedEmail.headers?.cc || '';
                                const ccEmailMatch = cc ? (cc.match(/<(.+?)>/) || cc.match(/([^\s<>]+@[^\s<>]+)/)) : null;
                                const ccEmailAddress = ccEmailMatch ? (ccEmailMatch[1] || ccEmailMatch[0]).trim().toLowerCase() : '';
                                
                                // Try to find student contact (prioritize student contacts)
                                let contact = fromEmailAddress ? emailContacts[fromEmailAddress] : null;
                                if (!contact || contact.contact_type !== 'student') {
                                  contact = toEmailAddress ? emailContacts[toEmailAddress] : null;
                                }
                                if (!contact || contact.contact_type !== 'student') {
                                  contact = ccEmailAddress ? emailContacts[ccEmailAddress] : null;
                                }
                                
                                const studentUnid = contact?.contact_type === 'student' && contact.reference_id 
                                  ? contact.reference_id 
                                  : null;
                                
                                if (studentUnid && linked.length === 0) {
                                  // Auto-add if student is detected and not yet linked
                                  addEmailToStudentFile(selectedEmail, [studentUnid]);
                                } else {
                                  // Open modal for selection
                                  setEmailForStudentSelect(selectedEmail);
                                  setSelectedStudentsForFile(linked.map(l => l.sophomorix_unid));
                                  setShowStudentSelectModal(true);
                                }
                              }}
                              className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors ${
                                linkedStudents[selectedEmail.id!] && linkedStudents[selectedEmail.id!].length > 0
                                  ? 'text-blue-600 dark:text-blue-400 hover:text-blue-700'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-50'
                              }`}
                              title={
                                linkedStudents[selectedEmail.id!] && linkedStudents[selectedEmail.id!].length > 0
                                  ? `Akten: Verknüpft mit ${linkedStudents[selectedEmail.id!].length} Schülerakte${linkedStudents[selectedEmail.id!].length > 1 ? 'n' : ''} (Klicken = verwalten)`
                                  : 'Akten: E-Mail in Schülerakte ablegen'
                              }
                            >
                              <FolderOpen className={`w-5 h-5 ${linkedStudents[selectedEmail.id!] && linkedStudents[selectedEmail.id!].length > 0 ? 'fill-current' : ''}`} />
                            </button>
                          )}
                          {!isDraftsFolder && (
                            <>
                              <button
                                onClick={() => {
                                  setEditingDraft(null);
                                  setReplyMode('reply');
                                  setOriginalEmail({
                                    from: getEmailFrom(selectedEmail),
                                    to: getEmailTo(selectedEmail) || '',
                                    cc: getEmailCc(selectedEmail) || '',
                                    subject: getEmailSubject(selectedEmail),
                                    body: selectedEmail.body || selectedEmail.text || '',
                                    html: selectedEmail.html || '',
                                    date: getEmailDate(selectedEmail),
                                    messageId: selectedEmail.headers?.messageId || undefined,
                                    inReplyTo: selectedEmail.headers?.inReplyTo || undefined
                                  });
                                  setShowComposeModal(true);
                                }}
                                className="p-2 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 rounded transition-colors"
                                title="Antworten"
                              >
                                <Reply className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingDraft(null);
                                  setReplyMode('replyAll');
                                  setOriginalEmail({
                                    from: getEmailFrom(selectedEmail),
                                    to: getEmailTo(selectedEmail) || '',
                                    cc: getEmailCc(selectedEmail) || '',
                                    subject: getEmailSubject(selectedEmail),
                                    body: selectedEmail.body || selectedEmail.text || '',
                                    html: selectedEmail.html || '',
                                    date: getEmailDate(selectedEmail),
                                    messageId: selectedEmail.headers?.messageId || undefined,
                                    inReplyTo: selectedEmail.headers?.inReplyTo || undefined
                                  });
                                  setShowComposeModal(true);
                                }}
                                className="p-2 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 rounded transition-colors"
                                title="Allen antworten"
                              >
                                <ReplyAll className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setEditingDraft(null);
                                  setReplyMode('forward');
                                  setOriginalEmail({
                                    from: getEmailFrom(selectedEmail),
                                    to: getEmailTo(selectedEmail) || '',
                                    cc: getEmailCc(selectedEmail) || '',
                                    subject: getEmailSubject(selectedEmail),
                                    body: selectedEmail.body || selectedEmail.text || '',
                                    html: selectedEmail.html || '',
                                    date: getEmailDate(selectedEmail),
                                    messageId: selectedEmail.headers?.messageId || undefined,
                                    inReplyTo: selectedEmail.headers?.inReplyTo || undefined
                                  });
                                  setShowComposeModal(true);
                                }}
                                className="p-2 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 rounded transition-colors"
                                title="Weiterleiten"
                              >
                                <Forward className="w-5 h-5" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDeleteEmail(selectedEmail)}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 hover:text-red-700 rounded transition-colors"
                            title="E-Mail löschen"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => toggleReadStatus(selectedEmail, !isRead(selectedEmail))}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                            title={isRead(selectedEmail) ? 'Lesebestätigung: Gelesen (Klicken = als ungelesen markieren)' : 'Lesebestätigung: Ungelesen (Klicken = als gelesen markieren)'}
                          >
                            {isRead(selectedEmail) ? (
                              <CheckCircle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                            ) : (
                              <Circle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span><strong>Von:</strong> {getEmailFrom(selectedEmail)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span><strong>An:</strong> {getEmailTo(selectedEmail) || 'N/A'}</span>
                        </div>
                        {/* Labels (Kategorien) */}
                        {selectedEmail.id && emailLabels[selectedEmail.id] && emailLabels[selectedEmail.id].length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span title="Label (Kategorie)"><Tag className="w-4 h-4 shrink-0" /></span>
                            <span className="text-gray-500 dark:text-gray-400 font-medium">Label:</span>
                            <div className="flex flex-wrap gap-1">
                              {emailLabels[selectedEmail.id].map((label) => (
                                <span
                                  key={label.id}
                                  className="email-category-chip cursor-pointer px-2 py-0.5 text-xs rounded-full hover:opacity-80 group"
                                  style={emailLabelChipStyle(label.color)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (selectedEmail.id) {
                                      console.log('[EmailClient] Label pill clicked to remove in detail:', label.name, 'from email', selectedEmail.id);
                                      removeLabelFromEmail(selectedEmail.id, label.id);
                                    }
                                  }}
                                  title="Klicken zum Entfernen"
                                >
                                  <Tag className="w-3 h-3" />
                                  {label.name}
                                  <X className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Label Management Button */}
                        {selectedEmail.id && (
                          <div className="relative">
                            <button
                              onClick={() => {
                                console.log('[EmailClient] Label menu button clicked in DETAIL for email:', {
                                  emailId: selectedEmail.id,
                                  emailUid: selectedEmail.uid,
                                  currentMenu: showLabelMenuInDetail,
                                  willOpen: !showLabelMenuInDetail
                                });
                                // Close list menu if open
                                setShowLabelMenuInList(null);
                                // Toggle detail menu
                                setShowLabelMenuInDetail(!showLabelMenuInDetail);
                              }}
                              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-50"
                              title="Label: E-Mail kategorisieren (z. B. Frist, Fehlzeit)"
                            >
                              <Tag className="w-4 h-4" />
                              <Plus className="w-3 h-3" />
                              <span>Labels verwalten</span>
                            </button>
                            {showLabelMenuInDetail && (
                              <div
                                ref={labelMenuRef}
                                className="email-floating-menu mt-2 min-w-[200px] max-h-60 overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Labels hinzufügen</div>
                                {labels.length === 0 ? (
                                  <div className="px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                                    Keine Labels verfügbar. Standard-Labels werden beim ersten Laden erstellt.
                                  </div>
                                ) : (
                                  labels.map((label) => {
                                    const isAssigned = selectedEmail.id && emailLabels[selectedEmail.id]?.some(l => l.id === label.id);
                                    return (
                                      <button
                                        key={label.id}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (selectedEmail.id) {
                                            console.log('[EmailClient] Label clicked in DETAIL:', label, 'isAssigned:', isAssigned, 'emailId:', selectedEmail.id);
                                            if (isAssigned) {
                                              removeLabelFromEmail(selectedEmail.id, label.id);
                                            } else {
                                              addLabelToEmail(selectedEmail.id, label.id);
                                            }
                                            // Close menu after selection
                                            setTimeout(() => {
                                              setShowLabelMenuInDetail(false);
                                            }, 100);
                                          } else {
                                            console.warn('[EmailClient] Selected email has no ID:', selectedEmail);
                                            toast.error('E-Mail-ID fehlt. Bitte Seite neu laden.');
                                          }
                                        }}
                                        className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2 ${
                                          isAssigned ? 'bg-gray-50 dark:bg-gray-800/60' : ''
                                        }`}
                                      >
                                        <div
                                          className="w-3 h-3 rounded-full"
                                          style={{ backgroundColor: label.color || '#6B7280' }}
                                        />
                                        <span>{label.name}</span>
                                        {isAssigned && <X className="w-3 h-3 ml-auto" />}
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {selectedEmail.headers?.cc && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            <span><strong>CC:</strong> {getEmailCc(selectedEmail)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{new Date(getEmailDate(selectedEmail)).toLocaleString('de-DE')}</span>
                        </div>
                        {selectedEmail.attachments.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Paperclip className="w-4 h-4" />
                            <span>{selectedEmail.attachments.length} Anhang{selectedEmail.attachments.length > 1 ? 'e' : ''}</span>
                          </div>
                        )}
                        {emailAttachmentsLoading && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">Anhänge werden geladen...</div>
                        )}
                        {emailAttachments.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {emailAttachments.map((att) => (
                              <div key={att.index} className="flex items-center justify-between bg-gray-50 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2">
                                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 min-w-0">
                                  <Paperclip className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                  <span className="truncate">{att.filename}</span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">({Math.round(att.size / 1024)} KB)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => {
                                      if (!selectedEmail.id) return;
                                      setPreviewAttachment({
                                        emailId: selectedEmail.id,
                                        index: att.index,
                                        filename: att.filename,
                                        contentType: att.contentType,
                                      });
                                    }}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800"
                                  >
                                    Vorschau
                                  </button>
                                  <button
                                    onClick={() => window.open(`/api/email-client/email/${selectedEmail.id}/attachments/${att.index}/download`, '_blank')}
                                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200"
                                  >
                                    Download
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email Body */}
                    <div className="prose max-w-none">
                      {emailBodyLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400 mr-2" />
                          <span className="text-gray-600 dark:text-gray-400">E-Mail-Inhalt wird geladen...</span>
                        </div>
                      ) : sanitizedSelectedEmailHtml ? (
                        <div 
                          dangerouslySetInnerHTML={{ __html: sanitizedSelectedEmailHtml }} 
                          className="email-body"
                        />
                      ) : (
                        <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-200">
                          {selectedEmail.text || selectedEmail.body || '(Kein Inhalt - klicken Sie auf die E-Mail, um den Inhalt zu laden)'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
            </>
          )}
        </div>

      {previewAttachment && (
        <EmailAttachmentPreviewModal
          isOpen={!!previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          emailId={previewAttachment.emailId}
          attachmentIndex={previewAttachment.index}
          filename={previewAttachment.filename}
          contentType={previewAttachment.contentType}
        />
      )}

      {/* Compose Email Modal */}
      <EmailModal
        isOpen={showComposeModal}
        onClose={() => {
          const wasEditingDraft = !!editingDraft;
          setShowComposeModal(false);
          setReplyMode(undefined);
          setOriginalEmail(undefined);
          setEditingDraft(null);
          queryClient.invalidateQueries(['email-client', 'scheduled']);
          if (wasEditingDraft) {
            refetchEmails();
          } else if (mailboxConfig.sent) {
            // Refresh emails after closing (in case email was sent)
            // Switch to SENT mailbox to show the sent email
            if (mailbox !== mailboxConfig.sent) {
              setMailbox(mailboxConfig.sent);
            } else {
              refetchEmails();
            }
          }
        }}
        replyMode={replyMode}
        originalEmail={originalEmail}
        initialDraft={editingDraft || undefined}
        to={undefined}
        context="email-client"
      />

      {/* Student Selection Modal for adding email to student file */}
      {showStudentSelectModal && emailForStudentSelect && (
        <div className="fixed inset-0 bg-gray-600/50 dark:bg-black/60 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative card shadow-xl max-w-2xl w-full mx-4 my-8 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {emailForStudentSelect.id && linkedStudents[emailForStudentSelect.id]?.length > 0
                  ? 'Schüler-Verknüpfungen verwalten'
                  : 'E-Mail zu Schülerakte hinzufügen'}
              </h3>
              <button
                onClick={() => {
                  setShowStudentSelectModal(false);
                  setEmailForStudentSelect(null);
                  setSelectedStudentsForFile([]);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {emailForStudentSelect.id && linkedStudents[emailForStudentSelect.id]?.length > 0
                  ? 'Bestehende Verknüpfungen können über das × entfernt werden. Weitere Schüler können unten hinzugefügt werden.'
                  : 'Wählen Sie einen oder mehrere Schüler aus, zu deren Akte diese E-Mail hinzugefügt werden soll:'}
              </p>
              
              <StudentSelectModalContent
                email={emailForStudentSelect}
                selectedStudents={selectedStudentsForFile}
                onSelectionChange={setSelectedStudentsForFile}
                linkedStudents={emailForStudentSelect.id ? (linkedStudents[emailForStudentSelect.id] || []) : []}
                onAdd={() => {
                  if (emailForStudentSelect && selectedStudentsForFile.length > 0) {
                    addEmailToStudentFile(emailForStudentSelect, selectedStudentsForFile);
                    setShowStudentSelectModal(false);
                    setEmailForStudentSelect(null);
                    setSelectedStudentsForFile([]);
                  }
                }}
                onRemove={(fileId) => {
                  if (emailForStudentSelect) {
                    removeEmailStudentLink(emailForStudentSelect, fileId);
                  }
                }}
                onClose={() => {
                  setShowStudentSelectModal(false);
                  setEmailForStudentSelect(null);
                  setSelectedStudentsForFile([]);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Student Selection Modal Component
function StudentSelectModalContent({
  email,
  selectedStudents,
  onSelectionChange,
  linkedStudents,
  onAdd,
  onRemove,
  onClose
}: {
  email: Email;
  selectedStudents: string[];
  onSelectionChange: (unids: string[]) => void;
  linkedStudents: Array<{sophomorix_unid: string, student_name: string, klasse: string, file_id?: string}>;
  onAdd: () => void;
  onRemove: (fileId: string) => void;
  onClose: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [klasseFilter, setKlasseFilter] = useState('');
  const { data: studentsData, isLoading } = useQuery(
    ['students', searchTerm, klasseFilter],
    async () => {
      const response = await api.get('/students', {
        params: {
          search: searchTerm || undefined,
          sortField: 'Name',
          sortDirection: 'asc'
        }
      });
      return response.data.data || [];
    }
  );

  const students = studentsData || [];
  const filteredStudents = students.filter((s: any) => {
    if (klasseFilter && s.Klasse !== klasseFilter) return false;
    return true;
  });

  const uniqueClasses: string[] = (Array.from(new Set(students.map((s: any) => s.Klasse as string))) as string[]).filter((k: string) => Boolean(k)).sort();

  const toggleStudent = (unid: string) => {
    onSelectionChange(
      selectedStudents.includes(unid)
        ? selectedStudents.filter(u => u !== unid)
        : [...selectedStudents, unid]
    );
  };

  return (
    <>
      <div className="mb-4">
        <div className="flex gap-2 mb-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
            <input
              type="text"
              placeholder="Schüler suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 text-sm"
            />
          </div>
          <select
            value={klasseFilter}
            onChange={(e) => setKlasseFilter(e.target.value)}
            className="select input-field w-auto text-sm"
          >
            <option value="">Alle Klassen</option>
            {uniqueClasses.map((klasse) => (
              <option key={klasse} value={klasse}>{klasse}</option>
            ))}
          </select>
        </div>
      </div>

      {linkedStudents.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Bereits verknüpft mit:</p>
          <div className="flex flex-wrap gap-2">
            {linkedStudents.map((linked) => (
              <span
                key={linked.sophomorix_unid}
                className="inline-flex items-center gap-1 pl-2 pr-1 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded text-xs"
              >
                <Check className="w-3 h-3 flex-shrink-0" />
                <span>{linked.student_name} ({linked.klasse})</span>
                {linked.file_id && (
                  <button
                    onClick={() => onRemove(linked.file_id!)}
                    className="ml-1 p-0.5 rounded hover:bg-blue-200 hover:text-red-600 transition-colors"
                    title="Verknüpfung entfernen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        {isLoading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Lade Schüler...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <p className="text-sm">Keine Schüler gefunden</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredStudents.map((student: any) => {
              const isSelected = selectedStudents.includes(student.sophomorixUnid);
              const isLinked = linkedStudents.some(l => l.sophomorix_unid === student.sophomorixUnid);
              
              return (
                <button
                  key={student.sophomorixUnid}
                  onClick={() => !isLinked && toggleStudent(student.sophomorixUnid)}
                  disabled={isLinked}
                  className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                    isSelected ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                  } ${isLinked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    } ${isLinked ? 'bg-gray-200 border-gray-300' : ''}`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                      {isLinked && <Check className="w-3 h-3 text-gray-500 dark:text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                        {student.Vorname} {student.Name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{student.Klasse}</p>
                    </div>
                    {isLinked && (
                      <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">Bereits verknüpft</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="btn btn-outline btn-sm"
        >
          Abbrechen
        </button>
        <button
          onClick={onAdd}
          disabled={selectedStudents.length === 0}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Zu {selectedStudents.length} Schülerakte{selectedStudents.length !== 1 ? 'n' : ''} hinzufügen
        </button>
      </div>
    </>
  );
}
